import "@blueprintjs/select/lib/css/blueprint-select.css";
import { Menu, MenuItem, Tag, TagProps } from "@blueprintjs/core";
import { ItemListRendererProps, ItemRenderer, MultiSelect2, SelectPopoverProps } from "@blueprintjs/select";
import React, { useState } from "react";
import "./TagsMultiSelect.scss";
import VirtualList from "react-tiny-virtual-list";

export type GetTagName<T> = (tag: T) => string;
export type GetTagStyle<T> = (tag: T) => React.CSSProperties;
export type GetTagKey<T> = (tag: T) => string;
export type TagQueryMatcher<T> = (query: string, tag: T) => boolean;

interface TagsMultiSelectProps<T> {
  tags: T[];
  selectedTags: T[];
  onChange: (tags: T[]) => void;
  getTagName: GetTagName<T>;
  getTagStyle: GetTagStyle<T>;
  getTagKey: GetTagKey<T>;
  tagQueryMatcher: TagQueryMatcher<T>;
  className?: string;
  small?: boolean;
  popoverProps?: SelectPopoverProps["popoverProps"];
}

function fastHighlightText(text: string, query: string): React.ReactNode {
  if (query.trim() === "") {
    return [text];
  }

  const queryStart = text.toLowerCase().indexOf(query.toLowerCase());

  return (
    <>
      {text.substring(0, queryStart)}
      <strong>{text.substring(queryStart, queryStart + query.length)}</strong>
      {text.substring(queryStart + query.length)}
    </>
  );
}

const itemSize = 30;
const MAX_LIST_HEIGHT = 350;

function createListRenderer<T>() {
  return function renderList({ filteredItems, renderItem, menuProps }: ItemListRendererProps<T>) {
    const listItems = filteredItems;

    return (
      <Menu {...menuProps} className="GoodFillTagsMultiSelect__menu">
        <VirtualList
          height={listItems.length * itemSize < MAX_LIST_HEIGHT ? listItems.length * itemSize : MAX_LIST_HEIGHT}
          itemCount={listItems.length}
          itemSize={itemSize}
          renderItem={({ index, style }) => {
            const item = listItems[index];

            const renderedItem = renderItem(item as T, index);
            // This `renderedItem?.props` hack is necessary to pass VirtualList styles down into the MenuItem
            return <MenuItem {...renderedItem?.props} key={index} style={style} />;
          }}
        />
      </Menu>
    );
  };
}

const POPOVER_PROPS_DEFAULT: SelectPopoverProps["popoverProps"] = {
  minimal: true,
  captureDismiss: true,

  // make this false by default to make it work in narrow table filters
  // for other uses, where its too big, and we don't mind horizontal scroll
  // we can set it to true
  matchTargetWidth: false,
};

export function TagsMultiSelect<T>({
  tags,
  selectedTags,
  onChange,
  getTagName,
  getTagStyle,
  getTagKey,
  tagQueryMatcher,
  className,
  small,
  popoverProps,
}: TagsMultiSelectProps<T>) {
  const [query, setQuery] = useState("");

  const itemRenderer: ItemRenderer<T> = (item, { modifiers, handleClick, query }) => (
    <MenuItem
      active={modifiers.active}
      key={getTagKey(item)}
      onClick={handleClick}
      text={<Tag style={getTagStyle(item)}>{fastHighlightText(getTagName(item), query)}</Tag>}
      shouldDismissPopover={false}
    />
  );

  const getTagProps = (value: React.ReactNode): TagProps => {
    const foundItem = selectedTags.find((item) => value === item);
    const style: React.CSSProperties = foundItem !== undefined ? getTagStyle(foundItem) : {};

    return {
      style: { ...style },
      // This is hack to make remove icon [x] black if text color is black
      minimal: style.color === "black",
    };
  };

  const availableTagsToBeSelected = tags.filter(
    (tag) => selectedTags.find((selectedTag) => selectedTag === tag) === undefined,
  );

  const onRemove = (index: number) => {
    const copySelectedTags = [...selectedTags];
    copySelectedTags.splice(index, 1);

    return onChange(copySelectedTags);
  };

  const onSelect = (tag: T) => onChange([...selectedTags, tag]);
  const filteredAvailableTags = availableTagsToBeSelected.filter((tag) => tagQueryMatcher(query, tag));

  return (
    <MultiSelect2<T>
      onQueryChange={setQuery}
      resetOnSelect={true}
      itemListRenderer={createListRenderer<T>()}
      itemRenderer={itemRenderer}
      items={filteredAvailableTags}
      onItemSelect={onSelect}
      // This is only renderer of the INNER part of the tag
      tagRenderer={getTagName}
      popoverProps={{
        ...POPOVER_PROPS_DEFAULT,
        ...(popoverProps !== undefined ? popoverProps : {}),
      }}
      tagInputProps={{
        onRemove: (_, index) => onRemove(index),
        tagProps: getTagProps,
      }}
      selectedItems={selectedTags}
      className={`GoodFillTagsMultiSelect ${small ? "GoodFillTagsMultiSelect__small" : ""} ${className ?? ""}`}
    />
  );
}
